import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
} from '@sola/ark-core/molecules/sheet/sheet.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';

import { CommitmentAvatarGroup } from '../commitment-avatar-group/commitment-avatar-group';

export enum MakeAPaymentType {
  payAhead,
  paymentDue,
  missingPayment,
}

export interface MakeAPaymentProps {
  alertType: MakeAPaymentType;
  commitments?: GraphQLCommitment[];
  amountDue?: number;
  className?: string;
}

const MakeAPaymentSheetContent: React.FC = () => {
  const { t } = useTranslation('my');

  return (
    <SheetContent title={t('makeAPayment.title')}>
      <SheetHeader>{t('makeAPayment.title')}</SheetHeader>
      <SheetBody />
    </SheetContent>
  );
};

export const MakeAPayment = ({
  alertType,
  commitments,
  amountDue,
  className,
}: MakeAPaymentProps) => {
  const { t } = useTranslation(['my', 'common']);
  const [isOpen, setIsOpen] = useState(false);
  const variant =
    alertType === MakeAPaymentType.missingPayment ? 'negative' : 'neutral';
  const translationKey = MakeAPaymentType[alertType];
  return (
    <>
      <Alert
        className={className}
        noIcon
        mobileCentered
        title={t(`makeAPayment.alerts.${translationKey}.title`)}
        message={t(`makeAPayment.alerts.${translationKey}.message`, {
          amountDue: t('numbers.currency', {
            amount: `${amountDue}`,
          }),
        })}
        middleColumn={
          alertType !== MakeAPaymentType.payAhead &&
          commitments &&
          commitments?.length > 0 && (
            <CommitmentAvatarGroup
              commitments={commitments}
              purpose={variant}
              size="lg"
              tooltipMessage={t(
                `makeAPayment.alerts.${translationKey}.tooltip`,
                {
                  count: commitments.length,
                },
              )}
            />
          )
        }
        variant={variant}
        buttonAction={
          <button
            onClick={() => setIsOpen(true)}
            aria-label={t(`makeAPayment.alerts.cta`)}
            type="button"
          >
            {t(`makeAPayment.alerts.cta`)}
          </button>
        }
      />

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <MakeAPaymentSheetContent />
      </Sheet>
    </>
  );
};
